/**
 * The two OFFSETS a regex match yields, each under its own name.
 *
 * `m.index + m[0].length` is the single most repeated lexer idiom in this tree -- 337 sites across 232 files at the
 * 2026-08-25 census -- and it is spelled two ways that differ by one character and mean genuinely different things:
 *
 *   - WITHOUT `- 1` (192 sites) it is the EXCLUSIVE END of the match: the offset one past the last matched character,
 *     which is what `slice`, `substring` and a `lastIndex` assignment all want.
 *   - WITH `- 1` (145 sites) it is the INDEX OF THE LAST MATCHED CHARACTER, which is what a backward scan, a
 *     bracket-depth walk or a "what character closed this" test wants.
 *
 * THEY GET TWO NAMES RATHER THAN ONE PLUS AN ARGUMENT, and that is the whole point of the leaf. A single
 * `matchEnd(m, inclusive?)` would put the off-by-one back at the call site as a boolean nobody reads, which is the
 * defect the naming is meant to remove: the two spellings are near-identical on the page, so a reader checking a span
 * computation cannot tell a deliberate inclusive index from a forgotten `- 1`. Under two names the intent is in the
 * verb and a wrong one is visible.
 *
 * `RegExpExecArray` rather than `RegExpMatchArray` because `index` is optional on the latter -- every live site feeds
 * an `.exec()` result, and a site that did not would already fail to typecheck on the raw idiom for the same reason.
 */

import type { CharOffsetSpan } from '@federa/types/compliance-scan-span';

/**
 * The offset one PAST the last matched character -- the exclusive end of the match span.
 *
 * This is the offset to slice from, to resume a manual scan at, or to assign to `lastIndex`. It is NOT a valid index
 * into the string when the match runs to the end: for a match ending the input it equals `input.length`.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the canonical span derivation this leaf owns, pinned below the
 * utils layer floor because `const/` leaves bind it too and `const/` is the only layer every caller can reach.
 */
export function matchEnd(match: RegExpExecArray): number {
	return match.index + match[0].length;
}

/** The match as a `{ start, end }` offset span -- start at `match.index`, end EXCLUSIVE per {@link matchEnd} -- the
 *  record the span-collecting scanners each spelled as `{ start: m.index, end: matchEnd(m) }`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-file {@link matchEnd}. */
export function matchSpan(match: RegExpExecArray): CharOffsetSpan {
	return { start: match.index, end: matchEnd(match) };
}

/**
 * The text of `input` PAST a match -- `input.slice(matchEnd(match))` -- or null when there was no match: what a
 * reader anchored on a head (a declaration's `=`, an `enum:` key) reads the body after it from.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): derives from the same-file {@link matchEnd}, same floor.
 */
export function textAfterMatch(input: string, match: RegExpExecArray | null): string | null {
	return match === null ? null : input.slice(matchEnd(match));
}

/**
 * The text past a match with its leading whitespace dropped -- `input.slice(matchEnd(match)).trimStart()` -- what a
 * key reader inspects after the key it matched: the step-wall rule's declared wall and the second-record rule's
 * identifier tail each spelled it (2026-09-28 fold).
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): derives from the same-file {@link matchEnd}, same floor.
 */
export function trimmedTextAfterMatch(input: string, match: RegExpExecArray): string {
	return input.slice(matchEnd(match)).trimStart();
}

/**
 * Where a span that opened at `at` ENDS: the absolute index of `stop`, a match found on `source.slice(at)`, or the
 * source's end when nothing stopped it -- the bound a sentence walk and a trigger-body walk each spelled as
 * `stop === null ? source.length : at + stop.index` (2026-09-28 fold).
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the null-tolerant start-index sibling of {@link sliceMatchEnd}.
 */
export function stopIndexOrEnd(source: string, at: number, stop: RegExpExecArray | null): number {
	return stop === null ? source.length : at + stop.index;
}

/**
 * The index OF the last matched character -- one less than {@link matchEnd}.
 *
 * This is the offset to start a backward walk from, or to test as "the character this match closed on". Undefined in
 * meaning for a zero-length match, where it answers one BEFORE the match start; no live site passes one, and a caller
 * that might should test the match length rather than reading a negative-relative index.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the inclusive twin of {@link matchEnd}, held at the same layer
 * floor for the same reason -- the two names are one decision and must not split across layers.
 */
export function matchLastCharIndex(match: RegExpExecArray): number {
	return match.index + match[0].length - 1;
}

/**
 * CARVE a match that ENDS on an opening brace, together with the brace body it opens, out of `text`: answers that
 * body (braces included) and the text with both removed, or null when `readBraced` finds no balanced body. The brace
 * reader is injected because each caller owns its own string-aware walk; the slice arithmetic is the one decision.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the carve two `utils/` leaves (the flow shaper arm reader and the
 * flow output-schema parser) spelled three times over, which a `utils/` home would forbid them to share.
 */
export function carveMatchedBracedSpan(
	text: string,
	match: RegExpExecArray,
	readBraced: (text: string, openIdx: number) => string | null | undefined,
): { braced: string; rest: string } | null {
	const openIdx = matchLastCharIndex(match);
	const braced = readBraced(text, openIdx);
	if (!braced) return null;
	return { braced, rest: text.slice(0, match.index) + text.slice(openIdx + braced.length) };
}

/**
 * The ABSOLUTE exclusive end of a match found on a SLICE of its input that began at `sliceStart` -- {@link matchEnd}
 * re-based onto the whole input, which is the offset a lexer that peeks at `input.slice(at)` resumes the whole-input
 * scan at. The match's own index is counted, so an unanchored pattern re-bases correctly; an anchored one adds zero.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): derives from the same-file {@link matchEnd}, held at the same layer
 * floor -- the citation lexers that peek past a span (`const/doctrine-citation-binding.ts`,
 * `const/knowledge-record-citation-tuples.ts`) each spelled the re-base by hand.
 */
export function sliceMatchEnd(sliceStart: number, match: RegExpExecArray): number {
	return sliceStart + matchEnd(match);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the inclusive twin of {@link sliceMatchEnd} -- the whole-text index
 * of a slice match's LAST character, the opener a braced-body walk starts on when the head pattern ends on the `{`.
 * The git-spawn census and the schema-key rule each spelled `sliceMatchEnd(from, match) - 1`.
 */
export function sliceMatchLastCharIndex(sliceStart: number, match: RegExpExecArray): number {
	return sliceStart + matchLastCharIndex(match);
}

/**
 * The ABSOLUTE offset of the first `needle` inside the match text -- where an opener or a name the pattern consumed
 * sits in the whole input. Like the raw `match.index + match[0].indexOf(needle)` it replaces, a needle absent from the
 * match answers one before `match.index`; every caller passes a needle its own pattern consumed.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the in-match offset twin of {@link matchEnd}, held at the same layer
 * floor -- the SQL, JSX and record lexers each re-spelled the index arithmetic around their own opener.
 */
export function matchNeedleOffset(match: RegExpExecArray, needle: string): number {
	return match.index + match[0].indexOf(needle);
}

/** {@link matchNeedleOffset} for the LAST `needle` inside the match text -- the closing delimiter, or a name the
 *  pattern consumed after a prefix that could itself contain the needle.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the backward twin of {@link matchNeedleOffset}. */
export function matchLastNeedleOffset(match: RegExpExecArray, needle: string): number {
	return match.index + match[0].lastIndexOf(needle);
}

/**
 * Whether the match's OWN quote pair -- the glyph its pattern captured as group 1 and closed on -- are real string
 * DELIMITERS in the input rather than characters inside a longer literal: the screen that separates a respelled literal
 * from a call CITED in prose. The quote appears exactly twice in such a match (the value group excludes it), so its
 * first and last occurrence ARE the two delimiters, and both must sit at a column `delimiterColumns` holds.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): composes {@link matchNeedleOffset} and
 * {@link matchLastNeedleOffset}; two rule leaves that may not import each other spelled it under two private names.
 */
export function isMatchQuotePairDelimited(match: RegExpExecArray, delimiterColumns: ReadonlySet<number>): boolean {
	const quote = match[1] ?? '';
	return delimiterColumns.has(matchNeedleOffset(match, quote)) && delimiterColumns.has(matchLastNeedleOffset(match, quote));
}

/**
 * The ABSOLUTE offset just PAST the first `needle` inside the match -- where the body an opener introduces begins (the
 * column list after `(`, the element list after `[`, the record body after `{`).
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): {@link matchNeedleOffset} stepped over the needle, same floor.
 */
export function matchOffsetPast(match: RegExpExecArray, needle: string): number {
	return matchNeedleOffset(match, needle) + needle.length;
}

/**
 * The ABSOLUTE offset where the match's trailing `tail` begins -- {@link matchEnd} backed off by the tail's length, the
 * offset a lexer re-seats `lastIndex` at to re-read, or anchors a finding on, the capture its pattern ENDS with.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): derives from the same-file {@link matchEnd}, same floor.
 */
export function matchTailStart(match: RegExpExecArray, tail: string): number {
	return matchEnd(match) - tail.length;
}
